import React, { useState, useCallback, useEffect } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { Clear } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAccountingRecords } from 'src/shared/services/paymentService';
import { useTranslation } from 'react-i18next';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AccountingRecord } from '../models/accountingRecord';
import { PaymentAccountingSearchFilter } from '../models/paymentAccountingSearchFilter';
import { PaymentExportButton } from './paymentExportButton';

type PaymentAccountingDataGridProps = {
  searchFilter: PaymentAccountingSearchFilter;
};

const PaymentAccountingDataGrid = (props: PaymentAccountingDataGridProps) => {
  const { searchFilter } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const isMounted = useIsMounted();
  const [rows, setRows] = useState<AccountingRecord[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: false, // set to true later
    totalRows: 0, // set to other prop later
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'verificationNo', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getAccountingRecords(
        data.page,
        data.pageSize,
        sortModel[0].field,
        sortModel[0].sort,
        searchFilter.searchString,
        searchFilter.ownerId,
      );
      if (isMounted()) {
        ConsoleHelper.log(res.data.results);
        updateData('totalRows', res.data.totalItems);
        setRows(res.data.results);
      }
      ConsoleHelper.log(res);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  }, [data.page, data.pageSize, sortModel, searchFilter.searchString, searchFilter.ownerId, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'verificationNo',
      headerName: t('invoice:payment.gridColumns.verificationNo'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.verificationNo}</>;
      },
    },
    {
      field: 'mindate',
      headerName: t('invoice:payment.gridColumns.from'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.mindate}</>;
      },
    },
    {
      field: 'maxdate',
      headerName: t('invoice:payment.gridColumns.to'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.maxdate}</>;
      },
    },
    {
      field: 'sum',
      headerName: t('invoice:payment.gridColumns.sum'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { sum } = params.row;
        return (sum && <AmountWithCurrency amount={sum} />) || '-';
      },
    },
    {
      field: 'count',
      headerName: t('invoice:payment.gridColumns.count'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.count}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
    >
      <DataGrid<AccountingRecord>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
      {selectionModel && selectionModel.length > 0 && searchFilter.ownerId && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            {/* <PaymentExportCSVButton selectionModel={selectionModel} rows={rows} /> */}
            <PaymentExportButton selectionModel={selectionModel} rows={rows} ownerId={searchFilter.ownerId} />
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};

export default PaymentAccountingDataGrid;
